/**
 * starField.js
 *
 * Deterministic generator for the landing sky's star field.
 *
 * A FAITHFUL PORT of the generator in
 * docs/design/landing-sky/landing-sky-reference.html — the approved design.
 * The reference is the source of truth for every constant here.
 *
 * WHY THE PORT HAS TO BE EXACT
 * ----------------------------
 * The PRNG is seeded (1337), so the sky is byte-identical on every load — that
 * is the point. But a seeded sequence is only reproducible if the CONSUMING
 * ORDER matches: every rnd() call, in the same order, including the ones the
 * reference makes conditionally. Two conditions consume a random number only
 * sometimes:
 *
 *   1. the small-tier opacity (`o`) — drawn only when tier === 's'
 *   2. the twinkle duration and delay — drawn only when the star twinkles
 *
 * Reordering these, or drawing them unconditionally to "tidy up", produces a
 * different sky that still looks plausible. The comments below mark each draw
 * so the sequence stays auditable against the reference.
 *
 * The output is plain data. Rendering is CSS (see LandingSky.css): no canvas,
 * no requestAnimationFrame, no animation library.
 *
 * SKY SCALE (k) — added October 2026
 * ---------------------------------
 * The reference's radius (1160) covers a 1280x760 viewport and nothing larger,
 * so on a wide or tall window the disc's curved edge became visible. The fix is
 * a single scale factor k applied to the GEOMETRY only: the pole, the radius,
 * and the line-reach thresholds. Star diameters and the counts are untouched.
 *
 * Two consequences worth stating, because both are deliberate:
 *
 *   - Every position in this generator derives from the radius, so multiplying
 *     the radius by k multiplies every x and y by exactly k. The same seed
 *     therefore yields the SAME pattern, spread wider — not a reshuffle. The
 *     reaches scale too, so the same star pairs get lines and the constellation
 *     figures keep their shapes.
 *   - The counts stay at the 1280 values, so a bigger sky is a SPARSER sky.
 *     That is the approved behaviour (60 visible stars at every size), not an
 *     oversight. Deriving the counts from the scaled radius instead would hold
 *     density constant and is explicitly not wanted.
 *
 * At k = 1 every value is bit-identical to the reference. Multiplication by 1.0
 * is exact in IEEE-754, so this is a guarantee rather than a near-miss.
 */

// ---------------------------------------------------------------------------
// Parameters of the approved design. Do not tune these without a new ruling —
// the sky Dennis approved on September 30, 2026 is the 60-star variant.
// ---------------------------------------------------------------------------
export const VISIBLE_STARS = 60; // foreground stars visible at any moment
export const AMBIENT_VISIBLE = 340; // faint background points visible at any moment
export const POLE_X = 700; // rotation pole, in the reference's 1280-wide space
export const POLE_Y = -120; // just above the top edge
export const DISC_R = 1160; // disc radius: covers the 1280x760 viewport at every angle
export const REF_W = 1280; // the reference composition's width
const REF_VISIBLE_H = 600; // the window height the density targets assume
const SEED = 1337;

/**
 * Whether to draw faint lines between nearby stars. OFF, and deliberately so.
 *
 * The lines were decorative: nearest-neighbour by distance, nothing more. A sky
 * that draws connections between stars reads as a claim that those connections
 * exist, and on this page they did not — so they are not drawn (ruled October 2,
 * 2026; reference rev. 4).
 *
 * The computation below is kept rather than deleted because a later phase intends
 * lines that DO carry meaning — shared tags between entries, most likely, the way
 * the Constellations graph already works. When that lands, this flag is where it
 * starts, but the admission rule has to change with it: distance is the wrong
 * criterion for a real relationship.
 *
 * While it is off, nothing is computed and `lines` comes back empty. Turning it
 * on cannot shift the star field — the line pass draws no random numbers.
 */
export const SHOW_STAR_LINES = false;

// Line reaches, in reference pixels. Scaled by k with everything else, so the
// same pairs are admitted at every size (see the header note). Dormant with
// SHOW_STAR_LINES off.
const REACH_LARGE = 260;
const REACH_MEDIUM = 170;

// ---------------------------------------------------------------------------
// Sky scale
// ---------------------------------------------------------------------------

/** The pole's offset to the right of the viewport centre, at k = 1: 700 - 640. */
export const POLE_OFFSET_X = POLE_X - REF_W / 2;
/** The pole's height above the top edge, at k = 1. POLE_Y is negative. */
const POLE_ABOVE_TOP = -POLE_Y;

/**
 * A little slack beyond exact corner contact. Exact contact would leave the
 * farthest corner pixel sitting precisely on the disc boundary, where px
 * rounding of the disc's size and offsets could expose a hairline of bare
 * gradient. 2% is far below the quantisation step, so it never costs a step.
 */
const COVER_MARGIN = 1.02;

/**
 * k is quantised to 1/20 (0.05) and always rounded UP, which is what keeps
 * resizing cheap: dragging a window edge crosses a step only occasionally, and
 * between steps nothing regenerates. Rounding up means the quantised k always
 * still satisfies the coverage requirement.
 */
const SCALE_STEP = 20;

/**
 * The smallest k (≥ 1, quantised) whose disc covers the whole viewport.
 *
 * The disc is a circle centred on the pole and it rotates about that centre, so
 * the region it covers is rotation-invariant — "at every rotation angle" is
 * satisfied automatically by a circle, and the condition reduces to: radius ≥
 * the distance from the pole to the farthest viewport corner.
 *
 * On screen the pole sits at (W/2 + POLE_OFFSET_X*k, POLE_Y*k) — horizontally
 * anchored to the viewport centre so the composition stays centred as the
 * window widens, which is how the 1280 design reads. Since the pole is always
 * right of centre and above the top edge, the farthest corner is always the
 * BOTTOM-LEFT one; verified numerically across viewports from 375x812 to
 * 3840x2160, where it holds in every case.
 *
 * Requiring  DISC_R*k ≥ COVER_MARGIN * hypot(W/2 + ox*k, H + oy*k)  and writing
 * A = DISC_R / COVER_MARGIN gives a quadratic in k:
 *
 *     (A² - ox² - oy²)·k²  -  (W·ox + 2H·oy)·k  -  (W²/4 + H²)  ≥  0
 *
 * The leading coefficient is positive (A² ≈ 1.29e6 against ox²+oy² = 18000) and
 * the constant term is negative, so the discriminant is always positive and the
 * larger root is the binding one. Closed form rather than a search loop: one
 * evaluation, no iteration count to tune.
 */
export function computeSkyScale(viewportWidth, viewportHeight) {
  const w = Math.max(1, viewportWidth);
  const h = Math.max(1, viewportHeight);
  const ox = POLE_OFFSET_X;
  const oy = POLE_ABOVE_TOP;

  const a = (DISC_R / COVER_MARGIN) ** 2 - ox * ox - oy * oy;
  const b = -(w * ox + 2 * h * oy);
  const c = -((w * w) / 4 + h * h);
  const raw = (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);

  // Integer step arithmetic so the k = 1 case lands on exactly 1.0: at
  // 1280x760 the raw value is ~0.987, Math.ceil(0.987 * 20) is 20, and 20 / 20
  // is exact. No float dust reaches the geometry.
  return Math.max(1, Math.ceil(raw * SCALE_STEP) / SCALE_STEP);
}

/**
 * mulberry32, exactly as the reference implements it (including the `seed |= 0`
 * and the `| 0` wraparound, which keep the arithmetic in int32 and are part of
 * the sequence). Closed over a local `seed` so each call to generateStarField()
 * starts from the same state and is independent of call order.
 */
function makeRandom(initialSeed) {
  let seed = initialSeed;
  return function rnd() {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Build the whole field once. Pure and deterministic: same (seed, skyScale) in,
 * same output every time. Call it inside a useMemo keyed on skyScale so it runs
 * when the scale changes rather than on every render.
 *
 * @param {number} [skyScale=1] k, from computeSkyScale(). Multiplies the
 *   geometry only — radius, positions, line reaches — never the counts or the
 *   star diameters. Pass 1 to reproduce the reference exactly.
 *
 * Coordinates are in the disc's own space, [0, 2 * DISC_R * k] on both axes. The
 * disc element is positioned so that its centre sits on the pole; the caller
 * owns that placement (see LandingSky.js).
 */
export function generateStarField(skyScale = 1) {
  const rnd = makeRandom(SEED);
  const k = skyScale;
  const radius = DISC_R * k;

  // The sky is a disc much larger than the viewport, so the counts above —
  // which are "visible at any moment" targets — scale up by the ratio of disc
  // area to visible area. Keeps density right while the disc rotates.
  //
  // DELIBERATELY the UNSCALED constants, not `radius`: using the scaled radius
  // here would raise the counts by k² and hold density constant as the window
  // grows. The approved behaviour is the opposite — same stars, spread wider,
  // so a larger sky is a sparser sky. Changing DISC_R to radius on this line is
  // the single most likely way to break that ruling.
  const scale = (Math.PI * DISC_R * DISC_R) / (REF_W * REF_VISIBLE_H);

  // Uniform sampling over the disc: sqrt() on the radius, or points crowd the
  // centre. TWO rnd() draws, radius first — order matters, see the header note.
  //
  // Both terms carry the same `radius`, so every coordinate is exactly k times
  // what it would be at k = 1. That identity is what makes the pattern scale
  // rather than reshuffle.
  const inDisc = () => {
    const rr = radius * Math.sqrt(rnd());
    const th = rnd() * Math.PI * 2;
    return { x: radius + rr * Math.cos(th), y: radius + rr * Math.sin(th) };
  };

  // -------------------------------------------------------------------------
  // Ambient field: hundreds of faint points rendered as the box-shadow of ONE
  // element, so the browser paints them in a single pass instead of mounting
  // ~1,900 DOM nodes. Four draws per point: radius, angle, size flag, alpha.
  // -------------------------------------------------------------------------
  const shadowParts = [];
  const ambientCount = Math.round(AMBIENT_VISIBLE * scale);
  for (let i = 0; i < ambientCount; i++) {
    const q = inDisc();
    const big = rnd() < 0.15;
    const alpha = 0.12 + rnd() * 0.33;
    shadowParts.push(
      `${q.x.toFixed(0)}px ${q.y.toFixed(0)}px 0 ${big ? '0.4px' : '0'} rgba(220,230,232,${alpha.toFixed(2)})`
    );
  }
  const ambientBoxShadow = shadowParts.join(',');

  // -------------------------------------------------------------------------
  // Foreground stars, in three size tiers. The tiers echo the Constellations
  // graph's star sizing (1-2, 3-9, 10+ entries per document) — a visual rhyme
  // with the authenticated product, not a data binding. These stars are
  // synthetic and represent no real CKG data.
  // -------------------------------------------------------------------------
  const stars = [];
  const starCount = Math.round(VISIBLE_STARS * scale);
  for (let j = 0; j < starCount; j++) {
    const r = rnd(); // draw 1 — tier
    const tier = r < 0.07 ? 'l' : r < 0.31 ? 'm' : 's';

    const p = inDisc(); // draws 2 and 3 — position
    const hue = rnd(); // draw 4 — colour bucket
    const color = hue < 0.12 ? '#FFD7AE' : hue < 0.24 ? '#CFEDEE' : '#F6F3EA';

    // draw 5 — diameter. One draw on every branch, so the sequence is stable
    // whichever tier this star is. NOT scaled by k: a star is a star at every
    // window size, which is what makes a bigger sky read as deeper rather than
    // as a zoom.
    const d = tier === 'l' ? 6.5 + rnd() : tier === 'm' ? 3.6 + rnd() * 0.9 : 2 + rnd() * 0.6;

    // CONDITIONAL DRAW: small stars vary in opacity; medium and large are
    // fixed literals and consume nothing. Drawing here unconditionally would
    // desynchronise every star after the first non-small one.
    const opacity = tier === 's' ? 0.55 + rnd() * 0.25 : tier === 'm' ? 0.88 : 1;

    const glow =
      tier === 'l'
        ? '0 0 10px 2px rgba(246,243,234,.55),0 0 30px 7px rgba(150,205,212,.16)'
        : tier === 'm'
          ? '0 0 6px 1px rgba(246,243,234,.38)'
          : 'none';

    const twinkles = rnd() < 0.4; // draw — twinkle flag

    // CONDITIONAL DRAWS: duration and delay only when this star twinkles.
    // The negative delay starts each one mid-cycle so they are not in step.
    let twinkleDuration = null;
    let twinkleDelay = null;
    if (twinkles) {
      twinkleDuration = `${(3 + rnd() * 4).toFixed(1)}s`;
      twinkleDelay = `-${(rnd() * 6).toFixed(1)}s`;
    }

    stars.push({
      key: `s${j}`,
      x: p.x,
      y: p.y,
      tier,
      // Pre-computed so the render is a plain style object with no arithmetic.
      left: `${(p.x - d / 2).toFixed(1)}px`,
      top: `${(p.y - d / 2).toFixed(1)}px`,
      size: `${d.toFixed(1)}px`,
      // A string, not a Number, to match the reference's toFixed(2) exactly.
      // `1` and `1.00` are the same opacity, but keeping the emitted text
      // identical means a future diff against the reference stays meaningful.
      opacity: opacity.toFixed(2),
      color,
      glow,
      twinkles,
      twinkleDuration,
      twinkleDelay
    });
  }

  // -------------------------------------------------------------------------
  // Faint connecting lines between nearby stars. OFF — see SHOW_STAR_LINES.
  //
  // Everything below is dormant and runs only if the flag is turned back on.
  // Kept rather than deleted because the intent is to bring lines back once they
  // mean something; this is the geometry half of that, already scale-correct.
  //
  // What it does when enabled: large stars reach to their 3 nearest neighbours
  // (within 260px), medium stars to their 1 nearest (within 170px), deduplicated
  // so a pair is only drawn once.
  //
  // The reaches scale by k along with the positions. That is not cosmetic: all
  // inter-star distances are exactly k times their k=1 values, so scaling the
  // thresholds by the same k admits exactly the same pairs. Leaving the reaches
  // fixed would silently dissolve the figures as the window grew.
  //
  // Consumes no randomness, so whether it runs or not cannot affect the star
  // sequence above. That is why switching it off leaves the sky untouched.
  // -------------------------------------------------------------------------
  const lines = [];

  if (SHOW_STAR_LINES) {
    const seen = new Set();
    for (let a = 0; a < stars.length; a++) {
      const wanted = stars[a].tier === 'l' ? 3 : stars[a].tier === 'm' ? 1 : 0;
      if (!wanted) continue;

      const near = [];
      for (let b = 0; b < stars.length; b++) {
        if (a === b) continue;
        const dx = stars[b].x - stars[a].x;
        const dy = stars[b].y - stars[a].y;
        near.push({ b, dist: Math.sqrt(dx * dx + dy * dy) });
      }
      near.sort((u, v) => u.dist - v.dist);

      const reach = (stars[a].tier === 'l' ? REACH_LARGE : REACH_MEDIUM) * k;
      for (let m = 0; m < wanted; m++) {
        const nb = near[m];
        if (!nb || nb.dist > reach) continue;
        const key = `${Math.min(a, nb.b)}-${Math.max(a, nb.b)}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const p1 = stars[a];
        const p2 = stars[nb.b];
        const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
        lines.push({
          key: `l${key}`,
          left: `${p1.x.toFixed(1)}px`,
          top: `${p1.y.toFixed(1)}px`,
          width: `${nb.dist.toFixed(1)}px`,
          transform: `rotate(${angle.toFixed(2)}deg)`,
          alpha: stars[a].tier === 'l' ? 0.22 : 0.13
        });
      }
    }
  }

  return {
    stars,
    lines,
    ambientBoxShadow,
    discSize: 2 * radius,
    // Echoed back so the caller positions the disc from the same k the geometry
    // was built with, rather than recomputing it and risking a mismatch.
    skyScale: k
  };
}
